import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  Tabs,
  Tab,
} from "@mui/material";
import CircularProgressBar from "./CircularProgressBar";
import Crown from "../../../SVGs/Crown";
import BroadcastRadio from "../../../SVGs/BroadcastRadio";
import Spinner from "../../../SVGs/Spinner";
import Competition from "../../../SVGs/Competition";
import Challenge from "../../../SVGs/Challenge";
import Survey from "../../../SVGs/Survey";
import CreateChannel from "../../../SVGs/CreateChannel";
import Quiz from "../../../SVGs/Quiz";
import InteractiveBook from "../../../SVGs/InteractiveBook";
import Ask from "../../../SVGs/Ask";
import SendReact from "../../../SVGs/SendReact";
import RequestIcon from "../../../SVGs/RequestIcon";

function getMilestoneIndex(milestones, label) {
  return milestones.findIndex((milestone) => milestone.label === label);
}

function CustomIconButton({
  iconSrc,
  label,
  onClick,
  milestone,
  currentProgress,
}) {
  const isActive = milestone.index <= currentProgress;
  return (
    <Tab
      label={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              transition: "0.5s all",
              fill: isActive ? "#EA5069" : "#c8c8c8",
            }}
          >
            <Crown />
          </Box>
          <IconButton
            sx={{
              backgroundColor: isActive ? "#25AAE1" : "#eeeeee",
              borderRadius: "50%",
              width: "calc(50px + 0.3vh)",
              height: "calc(50px + 0.3vh)",
              fill: isActive ? "#fff" : "#7a7a7a",
              stroke: isActive ? "#fff" : "#7a7a7a",
              transition: "0.5s all",
              "&:hover": {
                backgroundColor: "#D5F3FF",
                fill: "#25AAE1",
                stroke: "#25AAE1",
              },
              pointerEvents: "all",
            }}
            onClick={onClick}
          >
            {iconSrc}
          </IconButton>
          <Typography variant="caption">{label}</Typography>
        </Box>
      }
      sx={{
        opacity: "1",
      }}
    />
  );
}

function ProgressBar({ milestones, currentProgress }) {
  const isLargeScreen = useMediaQuery("(min-width: 1000px)");
  const progressBarRef = useRef(null);

  useEffect(() => {
    const progressBar = progressBarRef.current;
    const progressBarWidth = progressBar.offsetWidth;

    if (currentProgress >= 0) {
      const activeMilestoneWidth =
        (progressBarWidth / (milestones.length - 1)) * currentProgress;
      const progressWidth = (activeMilestoneWidth / progressBarWidth) * 100;

      const progressElement = progressBar.firstChild;
      progressElement.style.width = `${progressWidth}%`;
    }
  }, [currentProgress, milestones.length]);

  return (
    <Box
      ref={progressBarRef}
      sx={{
        width: "90%",
        height: "2px",
        backgroundColor: "#e0e0e0",
        borderRadius: "5px",
        position: "absolute",
        zIndex: "-1",
      }}
    >
      {currentProgress >= 0 && (
        <Box
          sx={{
            height: "100%",
            transition: "1s all",
            backgroundColor: "#25AAE1",
            borderRadius: "5px",
            position: "relative",
          }}
        ></Box>
      )}
    </Box>
  );
}

function FeaturesBar() {
  const [currentProgress, setCurrentProgress] = useState(-1);

  const milestones = [
    {
      label: "Broadcast Radio",
      icon: <BroadcastRadio />,
      index: 0,
    },
    { label: "Spinner", icon: <Spinner />, index: 1 },
    { label: "Competition", icon: <Competition />, index: 2 },
    { label: "Challenge", icon: <Challenge />, index: 3 },
    { label: "Survey", icon: <Survey />, index: 4 },
    { label: "Create Channel", icon: <CreateChannel />, index: 5 },
    { label: "Quiz", icon: <Quiz />, index: 6 },
    { label: "Interactive Book", icon: <InteractiveBook />, index: 7 },
    { label: "Ask", icon: <Ask />, index: 8 },
    { label: "Request", icon: <RequestIcon />, index: 9 },
    { label: "Send React", icon: <SendReact />, index: 10 },
  ];

  const handleClick = (label, index) => {
    const milestoneIndex = getMilestoneIndex(milestones, label);
    setCurrentProgress(milestoneIndex);
    setValue(index);
  };

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        p: "1em 2em",
        gap: "1em",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography fontWeight="bold">Try The Features</Typography>
        <CircularProgressBar />
      </Box>

      <Box
        sx={{
          display: "flex",
          whiteSpace: "nowrap",
          position: "relative",
          justifyContent: "center",
          alignItems: "center",
          overflow: "auto",
          maxWidth: "100%",

          flex: "1",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            maxWidth: "100%",
            position: "relative",
            width: "100%",
            "& .MuiTabs-flexContainer": {
              justifyContent: "space-between",
              width: "100%",
            },
          }}
          indicatorColor="none"
        >
          {milestones.map((milestone, index) => (
            <CustomIconButton
              key={milestone.label}
              iconSrc={milestone.icon}
              label={milestone.label}
              onClick={() => handleClick(milestone.label, index)}
              milestone={milestone}
              currentProgress={currentProgress}
            />
          ))}
        </Tabs>
        <ProgressBar
          milestones={milestones}
          currentProgress={currentProgress}
        />
      </Box>
    </Box>
  );
}

export default FeaturesBar;
